/**
 * Model downloads owned by the native app - the dictation models
 * (`dictation-models.ts`) and the reply voices (`pocket-voice.ts`) - and how
 * the UI reads their progress. Each download reports under its own event.
 */
import { listen } from "@tauri-apps/api/event";

import { isTauri } from "@/lib/is-tauri";

export type VoiceAssetProgress = {
  downloaded_bytes: number;
  total_bytes: number;
  file_index: number;
  file_count: number;
};

/** Progress of one download, under its own event name. */
export function onAssetProgress<P extends VoiceAssetProgress>(
  event: string,
  handler: (progress: P) => void,
): () => void {
  if (!isTauri()) return () => {};
  const unlisten = listen<P>(event, (e) => handler(e.payload));
  return () => {
    void unlisten.then((off) => off()).catch(() => {});
  };
}

/** Whole megabytes, for display. Rounded, so 587.9MB reads as 588 rather than
 *  as 587 with the last megabyte unaccounted for. */
export function megabytes(bytes: number): number {
  return Math.round(bytes / 1024 / 1024);
}

/**
 * A percentage that never reads as finished before it is.
 *
 * `Math.round` would show 100% with megabytes still to fetch, on a screen whose
 * whole job is telling someone how much longer they are waiting. Floor it, and
 * hold at 99 until the bytes actually agree.
 */
export function percentComplete(done: number, total: number): number {
  if (total <= 0) return 0;
  if (done >= total) return 100;
  return Math.min(99, Math.floor((done / total) * 100));
}
